import { apiFetch } from './api';

/**
 * La devolución de una validación: qué pasó con lo que la gente dijo.
 *
 * Es la contracara de `documentos.ts`. Aquel sirve para COMENTAR y este para
 * VER EN QUÉ QUEDÓ, y por eso son endpoints distintos con permisos distintos:
 * comentar lo puede cualquier miembro desde el primer día, ver el resultado
 * solo cuando quien facilita lo publica.
 */

export interface Voz {
  autor: string | null;
  texto: string | null;
  /** Vino de una nota de voz, no de un teclado. */
  de_viva_voz: boolean;
  aporte_id: string;
  dicho_at: string;
}

export interface LineaContexto {
  orden: number;
  /** La etiqueta de la fila: «Rol», «Criterios de aceptación»… */
  campo: string | null;
  texto: string;
  /** La línea que efectivamente recibió el comentario. */
  comentada: boolean;
}

export interface ParrafoResultado {
  bloque_id_estable: string;
  ref: string | null;
  orden: number;
  tipo: string;
  /** Lo que el documento dice hoy. */
  original: string;
  voces: Voz[];
  /** convergente · complementaria · contradictoria · sin_cambio. Null si la
   *  síntesis todavía no llegó a este párrafo. */
  clase: string | null;
  propuesta: string | null;
  final: string | null;
  motivo: string | null;
  /** En una contradicción no hay propuesta: hay posturas enfrentadas. */
  posiciones: Record<string, unknown> | null;
  estado_cambio: string | null;
  /** Otros párrafos que quedan tocados si este cambia. */
  implicaciones: string[];
  /** El segmento completo: todas las líneas con el mismo código. Vacío cuando
   *  el párrafo se basta solo. */
  contexto: LineaContexto[];
}

export interface SeccionResultado {
  titulo: string;
  nivel: number | null;
  parrafos: ParrafoResultado[];
  comentarios: number;
}

export interface Resultado {
  actividad_id: string;
  titulo: string;
  documento: string | null;
  publicado: boolean;
  parrafos_totales: number;
  comentarios: number;
  /** Voces que el sistema no pudo colgar de ningún párrafo. Se muestran igual:
   *  un comentario sin dirección no deja de ser un comentario. */
  sin_ubicar: Voz[];
  secciones: SeccionResultado[];
}

export interface DocumentoIndice {
  actividad_id: string;
  titulo: string;
  documento: string | null;
  estado: string;
  publicado: boolean;
  parrafos: number;
  comentarios: number;
  personas: number;
  cambios: number;
  impactos_pendientes: number;
}

export interface PersonaIndice {
  nombre: string;
  comentarios: number;
  documentos: number;
}

export interface IndiceResultado {
  proceso_id: string;
  proceso: string;
  documentos: DocumentoIndice[];
  personas: PersonaIndice[];
  total_comentarios: number;
}

export const resultadoKeys = {
  indice: (procesoId: string) => ['resultado-indice', procesoId] as const,
  documento: (actividadId: string, persona: string | null, soloComentados: boolean) =>
    ['resultado-doc', actividadId, persona ?? '', soloComentados] as const,
};

export const resultadoApi = {
  indice: (procesoId: string): Promise<IndiceResultado> =>
    apiFetch<IndiceResultado>(`/procesos/${procesoId}/resultado`),

  documento: (
    actividadId: string,
    opciones: { persona?: string | null; soloComentados?: boolean } = {},
  ): Promise<Resultado> => {
    const p = new URLSearchParams();
    if (opciones.persona) p.set('persona', opciones.persona);
    if (opciones.soloComentados === false) p.set('solo_comentados', 'false');
    const cola = p.toString();
    return apiFetch<Resultado>(
      `/validaciones/${actividadId}/resultado${cola ? `?${cola}` : ''}`,
    );
  },

  publicar: (actividadId: string, publicado: boolean): Promise<DocumentoIndice> =>
    apiFetch<DocumentoIndice>(`/validaciones/${actividadId}/publicar`, {
      method: 'PATCH',
      json: { publicado },
    }),
};

/** Cómo se nombra cada clase de cambio de cara a quien validó. */
export const NOMBRE_CLASE: Record<string, string> = {
  convergente: 'Se recogió',
  complementaria: 'Se fusionó con otros',
  contradictoria: 'Hay desacuerdo',
  sin_cambio: 'Sin cambio',
};


/**
 * Un color estable por persona, derivado del nombre.
 *
 * En un documento validado por varias personas, saber DE QUIÉN es cada voz de
 * un vistazo importa tanto como leerla: es lo que deja ver que una sección la
 * trabajó una sola persona, o que dos discreparon sobre el mismo párrafo. El
 * color sale del nombre y no del orden de llegada, para que sea el mismo en
 * todas las pantallas y entre sesiones.
 *
 * Los seis tonos son de la paleta de marca y todos pasan contraste sobre
 * superficie clara y oscura. El color NUNCA es la única señal: el nombre está
 * siempre escrito al lado (accesibilidad y daltonismo).
 */
const TONOS = [
  'var(--color-primary-600)',
  'var(--color-cyan-600)',
  'var(--color-violet-600)',
  'var(--color-recognition)',
  'var(--color-info)',
  'var(--color-success)',
] as const;

export function tonoDe(nombre: string | null): string {
  if (!nombre) return 'var(--text-muted)';
  let h = 0;
  for (let i = 0; i < nombre.length; i += 1) h = (h * 31 + nombre.charCodeAt(i)) >>> 0;
  return TONOS[h % TONOS.length];
}

/**
 * Diff por palabras entre el texto original y el propuesto.
 *
 * Sin esto, «se propone» es un párrafo entero que hay que releer comparando a
 * ojo con el de arriba — y en una redacción jurídica de ocho renglones donde
 * cambiaron cuatro palabras, nadie las encuentra. Marcar lo que se va y lo que
 * entra convierte la propuesta en una decisión que se puede tomar en segundos.
 *
 * Es la subsecuencia común más larga, sobre palabras y no sobre caracteres:
 * partir por letras produce ruido ilegible en castellano.
 */
export type TrozoDiff = { texto: string; estado: 'igual' | 'sale' | 'entra' };

export function diffPalabras(antes: string, despues: string): TrozoDiff[] {
  const a = antes.split(/(\s+)/).filter(Boolean);
  const b = despues.split(/(\s+)/).filter(Boolean);
  // Matriz de subsecuencia común más larga. Los textos son párrafos, no
  // documentos: el costo cuadrático acá es irrelevante.
  const m = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      m[i][j] = a[i] === b[j] ? m[i + 1][j + 1] + 1 : Math.max(m[i + 1][j], m[i][j + 1]);
    }
  }
  const salida: TrozoDiff[] = [];
  const empujar = (texto: string, estado: TrozoDiff['estado']) => {
    const ultimo = salida[salida.length - 1];
    if (ultimo && ultimo.estado === estado) ultimo.texto += texto;
    else salida.push({ texto, estado });
  };
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      empujar(a[i], 'igual');
      i += 1;
      j += 1;
    } else if (m[i + 1][j] >= m[i][j + 1]) {
      empujar(a[i], 'sale');
      i += 1;
    } else {
      empujar(b[j], 'entra');
      j += 1;
    }
  }
  while (i < a.length) { empujar(a[i], 'sale'); i += 1; }
  while (j < b.length) { empujar(b[j], 'entra'); j += 1; }
  return salida;
}
